import { useState } from 'react'
import type { ReactNode } from 'react'
import { IconChevronDown, IconChevronRight, IconExternalLink } from '@tabler/icons-react'

/* Rail navigation primitives. Exports only components. */

interface NavGroupProps {
  label: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}

/** A collapsible section of the rail. */
export function NavGroup({ label, defaultOpen = true, children }: NavGroupProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="lh-nav-group">
      <button
        type="button"
        className="lh-nav-group-header"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>{label}</span>
        {open ? <IconChevronDown size={12} stroke={2.5} /> : <IconChevronRight size={12} stroke={2.5} />}
      </button>
      {open && <div className="lh-nav-list">{children}</div>}
    </div>
  )
}

interface NavItemProps {
  label: ReactNode
  icon?: ReactNode
  active?: boolean
  /** Live count; hidden when 0 so the rail stays quiet. */
  badge?: number
  /** Render the badge in a muted tone rather than critical red. */
  badgeTone?: 'critical' | 'muted'
  /**
   * Why this row's count could not be measured. When set and there is no positive
   * `badge`, a muted dash renders in the badge slot, with the reason as its
   * tooltip — so a count nobody could take does not read as the quiet rail of
   * "nothing needs attention". A positive `badge` always wins.
   */
  badgeUnmeasured?: string
  /** `coming-soon` renders visible-but-inert with a tooltip. */
  state?: 'ready' | 'coming-soon'
  comingSoonHint?: string
  external?: boolean
  /**
   * Tooltip. Consumers use this to surface the thematic name ("Data Voyages")
   * without cluttering the rail with a second line of text per item — at 20+
   * items the labels have to stay scannable.
   */
  title?: string
  onClick?: () => void
}

/**
 * A rail destination.
 *
 * Renders as a <button> rather than an <a> because navigation is handled by the
 * router; `external` items only add the affordance, the caller still decides
 * what opening it means.
 */
export function NavItem({
  label,
  icon,
  active,
  badge,
  badgeTone = 'critical',
  badgeUnmeasured,
  state = 'ready',
  comingSoonHint = 'Coming soon',
  external,
  title,
  onClick,
}: NavItemProps) {
  const isComingSoon = state === 'coming-soon'
  const tooltip = isComingSoon
    ? [title, comingSoonHint].filter(Boolean).join(' — ')
    : title
  const hasBadge = badge !== undefined && badge > 0
  return (
    <button
      type="button"
      className="lh-nav-item"
      data-active={active ? 'true' : undefined}
      data-state={isComingSoon ? 'coming-soon' : undefined}
      aria-current={active ? 'page' : undefined}
      aria-disabled={isComingSoon || undefined}
      title={tooltip || undefined}
      onClick={onClick}
    >
      {icon && <span className="lh-nav-icon">{icon}</span>}
      <span className="lh-nav-label">{label}</span>
      {external && <IconExternalLink size={12} stroke={2} aria-label="Opens in a new tab" />}
      {hasBadge && (
        <span className="lh-nav-badge" data-tone={badgeTone === 'muted' ? 'muted' : undefined}>
          {badge}
        </span>
      )}
      {!hasBadge && badgeUnmeasured && (
        // Muted whatever badgeTone says: a dash is not an alarm, and the critical
        // tone on it would claim the very count it is saying nobody took.
        <span
          className="lh-nav-badge"
          data-tone="muted"
          title={badgeUnmeasured}
          aria-label={`Not measured: ${badgeUnmeasured}`}
        >
          —
        </span>
      )}
    </button>
  )
}
